import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/AppLayout';
import PermissionGate from '../../components/PermissionGate';
import { S, Icon, Icons, StatusBadge, BtnPrimary, BtnSecondary, EmptyState, formatDate } from '../regureStyles';
import useClientsFunctions from './useClientsFunctions';
import NewClientPanel from './NewClientPanel';
import ImportClientsModal from './ImportClientsModal';

// D-09: table headers were plain, no sort controls. Add client-side
// sort per column (works on the current page; server pagination
// remains unchanged). Keys map to the client field they read.
const SORT_KEYS = {
  clientNumber: 'clientNumber',
  name:         'displayName',
  type:         'type',
  email:        'email',
  phone:        'phone',
  status:       'status',
  created:      'createdAt',
};

const ClientsListScreen = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState('asc');
  const TYPE_BADGE = {
    individual: { label: t('clientsModule.list.typeIndividual'), bg: '#e0e7ff', color: '#4338ca' },
    company:    { label: t('clientsModule.list.typeCompany'),    bg: '#fce7f3', color: '#9d174d' },
  };
  const toggleSort = (headerId) => {
    if (sortKey === headerId) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(headerId);
      setSortDir('asc');
    }
  };
  const {
    clients,
    total,
    search, setSearch,
    statusFilter, setStatusFilter,
    typeFilter, setTypeFilter,
    showNew, setShowNew,
    showImport, setShowImport,
    editingClient,
    page, setPage,
    loading,
    STATUS_OPTIONS, STATUS_LABELS,
    TYPE_OPTIONS, TYPE_LABELS,
    handleCloseNew,
    handleCreated,
    handleImportDone,
    totalPages,
  } = useClientsFunctions();

  if (loading) {
    return (
      <AppLayout>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: S.burgundy, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {showNew && <NewClientPanel onClose={handleCloseNew} onCreated={handleCreated} client={editingClient} />}
      {showImport && <ImportClientsModal onClose={() => setShowImport(false)} onDone={handleImportDone} />}

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <header style={{ padding: '20px 32px', borderBottom: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 12, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              {t('clientsModule.list.breadcrumbOperations')} {Icons.chevronRight(S.textMuted, 12)} <span style={{ color: S.textPrimary, fontWeight: 500 }}>{t('clientsModule.list.breadcrumbClients')}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary }}>{t('clientsModule.list.title')}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '8px 16px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 10 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{total}</div>
                <div style={{ fontSize: 10, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>{t('clientsModule.list.totalLabel')}</div>
              </div>
            </div>
            <PermissionGate resource='clients' action='create'>
              <BtnSecondary onClick={() => setShowImport(true)}>
                <Icon name='upload' color={S.textMuted} size={15} /> {t('clientsModule.list.importCsv')}
              </BtnSecondary>
              <BtnPrimary onClick={() => setShowNew(true)}>
                <Icon name='plus' color='#fff' size={16} /> {t('clientsModule.list.newClient')}
              </BtnPrimary>
            </PermissionGate>
          </div>
        </header>

        <div style={{ padding: '14px 32px', display: 'flex', gap: 10, alignItems: 'center', borderBottom: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.6)', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 380 }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <Icon name='search' color='#94a3b8' size={15} />
            </div>
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder={t('clientsModule.list.searchPlaceholder')}
              style={{ width: '100%', padding: '9px 12px 9px 36px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textPrimary, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textSecondary, cursor: 'pointer', outline: 'none' }}
          >
            {STATUS_OPTIONS.map(o => <option key={o} value={o}>{STATUS_LABELS[o]}</option>)}
          </select>
          <select
            value={typeFilter}
            onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textSecondary, cursor: 'pointer', outline: 'none' }}
          >
            {TYPE_OPTIONS.map(o => <option key={o} value={o}>{TYPE_LABELS[o]}</option>)}
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: S.textMuted }}>{t('clientsModule.list.totalCount', { count: total })}</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px' }}>
          {clients.length === 0 ? (
            <EmptyState
              icon='users'
              title={t('clientsModule.list.emptyTitle')}
              description={t('clientsModule.list.emptyDescription')}
              action={<PermissionGate resource='clients' action='create'><BtnPrimary onClick={() => setShowNew(true)}><Icon name='plus' color='#fff' size={16} /> {t('clientsModule.list.newClient')}</BtnPrimary></PermissionGate>}
            />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    { id: 'clientNumber', label: t('clientsModule.list.colClientNumber') },
                    { id: 'name',         label: t('clientsModule.list.colName') },
                    { id: 'type',         label: t('clientsModule.list.colType') },
                    { id: 'email',        label: t('clientsModule.list.colEmail') },
                    { id: 'phone',        label: t('clientsModule.list.colPhone') },
                    { id: 'status',       label: t('clientsModule.list.colStatus') },
                    { id: 'created',      label: t('clientsModule.list.colCreated') },
                  ].map(h => (
                    <th
                      key={h.id}
                      onClick={() => toggleSort(h.id)}
                      style={{ padding: '13px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: `2px solid ${S.borderLight}`, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                    >
                      {h.label}
                      {sortKey === h.id && (
                        <span style={{ marginLeft: 6, color: S.burgundy }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const src = [...clients];
                  if (sortKey && SORT_KEYS[sortKey]) {
                    const field = SORT_KEYS[sortKey];
                    src.sort((a, b) => {
                      const va = a?.[field] ?? '';
                      const vb = b?.[field] ?? '';
                      // Dates get compared as timestamps; everything else
                      // as lower-cased strings so 'A' and 'a' land together.
                      const na = (typeof va === 'string' && /\d{4}-\d{2}-\d{2}/.test(va)) ? new Date(va).getTime() : String(va).toLowerCase();
                      const nb = (typeof vb === 'string' && /\d{4}-\d{2}-\d{2}/.test(vb)) ? new Date(vb).getTime() : String(vb).toLowerCase();
                      if (na < nb) return sortDir === 'asc' ? -1 : 1;
                      if (na > nb) return sortDir === 'asc' ? 1 : -1;
                      return 0;
                    });
                  }
                  return src;
                })().map((c, i) => {
                  const typeBadge = TYPE_BADGE[c.type] || TYPE_BADGE.individual;
                  return (
                    <tr
                      key={c._id}
                      style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard, cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = S.bgHover)}
                      onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard)}
                      onClick={() => history.push(`/clients/${c._id}`)}
                    >
                      <td style={{ padding: '13px 14px' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: S.burgundy, textDecoration: 'underline', textDecorationColor: S.burgundy + '50' }}>{c.clientNumber || '—'}</span>
                      </td>
                      <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{c.displayName || '—'}</td>
                      <td style={{ padding: '13px 14px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.label}</span>
                      </td>
                      <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{c.email || '—'}</td>
                      <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{c.phone || '—'}</td>
                      <td style={{ padding: '13px 14px' }}><StatusBadge status={c.status} /></td>
                      <td style={{ padding: '13px 14px', fontSize: 12, color: S.textMuted }}>{formatDate(typeof c.createdAt === 'string' ? c.createdAt.slice(0, 10) : c.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ padding: '14px 32px', borderTop: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.8)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: S.textMuted }}>{t('clientsModule.list.pageOf', { page, total: totalPages })}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  style={{ width: 32, height: 32, borderRadius: 8, border: p === page ? `1px solid ${S.burgundy}` : `1px solid ${S.borderLight}`, background: p === page ? 'rgba(128,0,32,0.07)' : '#fff', color: p === page ? S.burgundy : S.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >{p}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ClientsListScreen;
